import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserNextLevel } from '../../api/hooks/User/useUserNextLevel'
import { useUserStep } from '../../api/hooks/User/useUserStep'
import { useAuthStore } from '../../store/useAuthStore'
import { useLearningStore } from '../../store/useLearningStore'
import useRewardStore from '../../store/rewardStore'
import AvatarDisplay from '../individual/AvatarDisplay'
import styles from '../../styles/user/ActivitySummaryNew.module.scss'

// Coins/gems earned per lesson (fixed server-side value from rewardTrigger.js)
const LESSON_COINS = 20
const LESSON_GEMS = 1

const ActivitySummaryBox = () => {
  const { getAnswerSummary, questions, userAnswers, accumulateLevelTestRound } = useLearningStore()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const updateUserPartial = useAuthStore((state) => state.updateUserPartial)
  const nextLevelMutation = useUserNextLevel()
  const userStepMutation = useUserStep()
  const { coins, gems, avatar, fetchRewardSummary } = useRewardStore()
  const userAvatarSrc = (user as any)?.AVATAR || null

  useEffect(() => {
    if (user?.USER_ID) fetchRewardSummary((user as any).USER_ID).catch(() => {})
  }, [(user as any)?.USER_ID])

  const summary = getAnswerSummary()
  const correct = summary.correct
  const isStudent = !user?.LEVEL_ID?.toString().startsWith('99')

  const detail_unit = questions[0]?.DETAIL_UNIT ?? ''
  const percentage =
    summary.total > 0 ? Math.round((summary.correct / summary.total) * 100) : 0

  const questionData = useMemo(() => {
    return questions.map((question) => {
      const userAnswer = userAnswers.find(
        (answer) => answer.questionId === question.QUESTION_ID
      )
      return {
        id: question.QUESTION_ID,
        text: `Question ${question.NO}`,
        isCorrect: userAnswer?.isCorrect ?? false,
      }
    })
  }, [questions, userAnswers])

  // ── 일반 학습자 처리 ──────────────────────────────────────────
  const handleStartNext = async () => {
    if (!user) return

    const nextStepData = {
      LEVEL_ID: user.LEVEL_ID,
      ID: user.ID,
      STEP: user.STEP,
    }

    try {
      const nextLevelResponse = await nextLevelMutation.mutateAsync(nextStepData)
      const stepResponse = await userStepMutation.mutateAsync({ ID: user.ID })
      const data = stepResponse.data

      let serverStep: number | null = null
      if (Array.isArray(data)) {
        serverStep = data[0]?.STEP ?? null
      } else if (data && typeof data === 'object') {
        serverStep = data.STEP
      }

      if (typeof serverStep === 'number') {
        const nextLevelId = (nextLevelResponse as any)?.nextLevelId
        updateUserPartial({
          STEP: serverStep,
          ...(nextLevelId ? { LEVEL_ID: nextLevelId } : {}),
        })
        if (serverStep % 5 === 0) {
          navigate('/learningQuizOnly')
        } else {
          navigate('/solver')
        }
      } else {
        navigate('/solver')
      }
    } catch (error) {
      console.error('Error during next step:', error)
      navigate('/solver')
    }
  }

  // ── 레벨 테스트 대상자 처리 ───────────────────────────────────
  const handleLevelTest = async () => {
    const Level = user?.LEVEL_ID
    accumulateLevelTestRound()  // 현재 라운드 답안 누적
    console.log('Level handleLevelTest ? ', Level)
    console.log('correct ? ', correct)
    console.log('isStudent ? ', isStudent)
    if (!Level) return

    if (correct > 1) {
      if (Level === 991114) {
        updateUserPartial({ LEVEL_ID: 991131 })
        console.log('31111 이동')
        navigate('/result', { replace: true })
      } else if (Level === 991137) {
        updateUserPartial({ LEVEL_ID: 991151 })
        console.log('51111 이동')
        navigate('/result', { replace: true })
      } else if (Level === 9911510) {
        updateUserPartial({ LEVEL_ID: 991171 })
        console.log('71111 이동')
        navigate('/result', { replace: true })
      } else if (Level === 991179) {
        updateUserPartial({ LEVEL_ID: 991191 })
        console.log('91111 이동')
        navigate('/result', { replace: true })
      } else if (Level === 9911910) {
        updateUserPartial({ LEVEL_ID: 9911111 })
        console.log('9911111 레벨테스트 시작')
        navigate('/solver/individual', { state: { userType: 'general' } })
      } else if (Level === 9911119) {
        console.log('9911119 레벨테스트 완료 → result')
        navigate('/result', { replace: true })
      } else {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/testNextLevel`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ Level }),
            }
          )
          if (!res.ok) throw new Error('Failed Level Test')
          const result = await res.json()
          console.log('result ? ', result)
          console.log('result.data ? ', result.data)
          if (result.success && result.data[0]?.LEVEL_ID) {
            updateUserPartial({ LEVEL_ID: result.data[0].LEVEL_ID })
          }
          navigate('/solver/individual', { state: { userType: 'general' } })
        } catch (err) {
          console.error('Failed Level Test:', err)
        }
      }
    } else {
      if (Level === 991131) {
        updateUserPartial({ LEVEL_ID: 991111 })
        console.log('991111로 이동')
        navigate('/solver/individual', { state: { userType: 'general' } })
      } else if (Level === 991151) {
        updateUserPartial({ LEVEL_ID: 991131 })
        console.log('991131 이동')
        navigate('/solver/individual', { state: { userType: 'general' } })
      } else if (Level === 991171) {
        updateUserPartial({ LEVEL_ID: 991151 })
        console.log('991151 이동')
        navigate('/solver/individual', { state: { userType: 'general' } })
      } else if (Level === 991191) {
        updateUserPartial({ LEVEL_ID: 991171 })
        console.log('991171 이동')
        navigate('/solver/individual', { state: { userType: 'general' } })
      } else {
        navigate('/result', { replace: true })
      }
    }
  }

  useEffect(() => {
    const handlePopState = () => {
      if (isStudent) {
        handleStartNext()
      } else {
        handleLevelTest()
      }
    }
    window.history.pushState(null, '', window.location.href)
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isStudent, user?.ID, user?.LEVEL_ID, user?.STEP, correct])

  // ── SVG semi-circle chart ─────────────────────────────────────
  const radius = 80
  const strokeWidth = 26
  const cx = 95
  const cy = 95
  const circumference = Math.PI * radius
  const offset = circumference - (percentage / 100) * circumference
  const arcPath = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`

  // ── 애니메이션 상태 ────────────────────────────────────────────
  const [animatedOffset, setAnimatedOffset] = useState(circumference)
  const [displayCoins, setDisplayCoins] = useState(0)
  const [displayGems, setDisplayGems] = useState(0)
  const [displayPct, setDisplayPct] = useState(0)

  useEffect(() => {
    // arc 애니메이션: 잠깐 뒤 실제 offset으로 전환 (CSS transition이 동작하도록)
    const arcTimer = setTimeout(() => setAnimatedOffset(offset), 80)

    // 카운트업 애니메이션 (ease-out cubic, 1.2초)
    const DURATION = 1200
    const STEPS = 36
    let step = 0
    const countTimer = setInterval(() => {
      step++
      const t = step / STEPS
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplayCoins(Math.round(LESSON_COINS * eased))
      setDisplayGems(Math.round(LESSON_GEMS * eased))
      setDisplayPct(Math.round(percentage * eased))
      if (step >= STEPS) clearInterval(countTimer)
    }, DURATION / STEPS)

    return () => {
      clearTimeout(arcTimer)
      clearInterval(countTimer)
    }
  }, [])

  return (
    <>
      {/* Coins / Gems overlay */}
      <div className={styles.rewardOverlay}>
        <div className={styles.rewardChip}>
          <img src="/assets/icons/coin.png" alt="coin" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <span className={styles.rewardChipValue}>{coins.toLocaleString()}</span>
        </div>
        <div className={styles.rewardChip}>
          <img src="/assets/icons/gem.png" alt="gem" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <span className={styles.rewardChipValue}>{gems}</span>
        </div>
      </div>

      <div className={styles.pageWrapper}>
        {/* Title */}
        <p className={styles.pageTitle}>Activity Summary <img src="/assets/icons/celebration.png" alt="celebration" style={{ width: 24, height: 24, objectFit: 'contain', verticalAlign: 'middle' }} /></p>
        <p className={styles.pageSubtitle}>Great job! Keep up the amazing work!</p>

        {/* Reward section */}
        <div className={styles.rewardSection}>
          <img
            src="/assets/summary-char-left.png"
            alt=""
            className={styles.rewardLeftChar}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
          <div className={styles.rewardCard}>
            <div className={styles.rewardItem}>
              <div className={styles.rewardIconCircle} style={{ background: '#fff8e6' }}>
                <img src="/assets/icons/coin.png" alt="coin" style={{ width: 36, height: 36, objectFit: 'contain' }} />
              </div>
              <div className={styles.rewardItemInfo}>
                <span className={styles.rewardItemLabel}>Coins Earned</span>
                <span className={styles.rewardItemValue}>+{displayCoins}</span>
              </div>
            </div>
            <div className={styles.rewardDivider} />
            <div className={styles.rewardItem}>
              <div className={styles.rewardIconCircle} style={{ background: '#f3eeff' }}>
                <img src="/assets/icons/gem.png" alt="gem" style={{ width: 36, height: 36, objectFit: 'contain' }} />
              </div>
              <div className={styles.rewardItemInfo}>
                <span className={styles.rewardItemLabel}>Gems Earned</span>
                <span className={styles.rewardItemValue}>+{displayGems}</span>
              </div>
            </div>
          </div>
          <img
            src="/assets/summary-chest-right.png"
            alt=""
            className={styles.rewardRightChest}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        </div>

        {/* Main card */}
        <div className={styles.mainCard}>
          {/* Left: character + username */}
          <div className={styles.charColumnWrapper}>
            <div className={styles.charColumn}>
              <AvatarDisplay
                avatar={{ ...avatar, background: null }}
                userAvatar={userAvatarSrc}
                size={185}
                showSparkles={false}
                naked={true}
                onClick={undefined}
              />
            </div>
            <span className={styles.charUsername}>
              {user?.NICKNAME || user?.NAME || 'Student'}
            </span>
          </div>

          {/* Center: topic + question list */}
          <div className={styles.topicColumn}>
            <p className={styles.topicLabel}>Lesson Topic</p>
            <p className={styles.topicTitle}>{detail_unit || 'Math Practice'}</p>
            <div className={styles.questionListWrapper}>
              {questionData.map((q) => (
                <div key={q.id} className={styles.questionRow}>
                  <span className={styles.questionText}>{q.text}</span>
                  {q.isCorrect ? (
                    <span className={styles.correctBadge}>Correct</span>
                  ) : (
                    <span className={styles.wrongBadge}>Wrong</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: progress chart */}
          <div className={styles.progressColumn}>
            <p className={styles.progressLabel}>Overall Progress</p>
            <div className={styles.percentageBadge}>
              <span className={styles.percentageValue}>{displayPct}%</span>
              <span className={styles.percentageCountLine}>
                {summary.correct} / {summary.total} Questions Correct
              </span>
            </div>
            <svg
              width="190"
              height="105"
              viewBox="0 0 190 105"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              overflow="visible"
            >
              <defs>
                <linearGradient id="arcGradA" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4f72f5" />
                  <stop offset="100%" stopColor="#2540c8" />
                </linearGradient>
              </defs>

              {/* 트랙 */}
              <path d={arcPath} stroke="#e8eaf0" strokeWidth={strokeWidth} strokeLinecap="round" fill="none" />

              {/* 채움 */}
              <path
                d={arcPath}
                stroke="url(#arcGradA)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={animatedOffset}
                fill="none"
                className={styles.arcAnimated}
              />
            </svg>
          </div>
        </div>

        {/* Bottom stats — unified card */}
        <div className={styles.statsCard}>
          <div className={styles.statSection}>
            <div className={styles.statIconCircle} style={{ background: '#eef0ff' }}>
              <img src="/assets/icons/target.png" alt="accuracy" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Accuracy</span>
              <span className={styles.statValue} style={{ color: '#4361ee' }}>
                {percentage}%
              </span>
              <span className={styles.statSub}>
                {summary.correct} Correct / {summary.total} Total
              </span>
            </div>
          </div>

          <div className={styles.statsDivider} />

          <div className={styles.statSection}>
            <div className={styles.statIconCircle} style={{ background: '#e8f8ef' }}>
              <img src="/assets/icons/check.png" alt="correct" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Correct</span>
              <span className={styles.statValue} style={{ color: '#2d9e5c' }}>
                {summary.correct}
              </span>
              <span className={styles.statSub}>Questions</span>
            </div>
          </div>

          <div className={styles.statsDivider} />

          <div className={styles.statSection}>
            <div className={styles.statIconCircle} style={{ background: '#fde8e8' }}>
              <img src="/assets/icons/wrong.png" alt="wrong" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statLabel}>Wrong</span>
              <span className={styles.statValue} style={{ color: '#e53e3e' }}>
                {summary.wrong}
              </span>
              <span className={styles.statSub}>Questions</span>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className={styles.buttonRow}>
          {isStudent && (
            <button
              className={styles.navyBtn}
              onClick={() => navigate('/dashboard/individual')}
            >
              Back to Dashboard
            </button>
          )}
          <button
            className={styles.orangeBtn}
            onClick={isStudent ? handleStartNext : handleLevelTest}
          >
            Start Next Activity! →
          </button>
        </div>
      </div>
    </>
  )
}

export default ActivitySummaryBox
